import { useSuspenseQuery } from '@apollo/client';

import { StartingChatMessage } from '@eluve/blocks';
import { useFeatureFlagPayload } from '@eluve/posthog-react';
import { useUserIdFromSession } from '@eluve/session-helpers';

import { ChatComponent } from './ChatComponent';
import { getChatConversationStartersQuery } from './chat.operations';

const DEFAULT_STARTING_MESSAGES = [
  {
    id: 'mcu-report',
    title: 'Summarize MCU Report',
    description:
      'Present conditions based on urgency, provide education, and recommend next steps.',
    content: `
Please generate a structured summary from the medical check-up (MCU) report. Extract findings, classify them by priority, and create a clear summary that helps guide clinical follow-up.

### INSTRUCTIONS
You will be given a single MCU report. Remember that this report was written by doctors for doctors, so you must consider explaining complex medical terms in simpler, patient-friendly language without misrepresenting the medical findings and doctor’s recommendations, where appropriate.

Your tasks are:
- Summarize relevant findings using both medical precision and patient-friendly explanations
- Identify all abnormal findings and diagnoses
- For each finding, present it concisely:
 1. **Bold medical finding** followed by *(simple term in italics)* if helpful
 2. Key test results or values from the report
 3. 👉 **One sentence** explaining what this means and why it matters
- Keep explanations brief - maximum 1-2 sentences per finding
- When mentioning test names, briefly explain what the test does (e.g., "a heart scan called CCTA")
- Classify each finding using the following categories:
 - 🔴 High Priority (needs urgent follow-up)
 - 🟠 Moderate Priority (requires treatment or monitoring)
 - 🟡 Low Priority (incidental or mild issues)
 - ✅ Normal Findings (no action needed)
- Format your response using the structure and labels provided
- Use only information present in the MCU document
- Do not copy or invent findings from the example below

---

### FORMATTING GUIDELINES
- Use Markdown formatting in your output
- Use larger section headers (level 2) for each priority level:
 * Use ## 🔴 High Priority (Needs Immediate Follow-Up)
 * Use ## 🟠 Moderate Priority (Monitor & Treat)
 * Use ## 🟡 Low Priority (Routine Monitoring)
 * Use ## ✅ Normal Findings (No Action Needed)
 * Use ## 📝 Doctor's Summary
- For each listed finding, begin with a bolded numbered label, e.g.: **1. [Condition Name]**
- Bold the word "Action" in every action recommendation line: **Action:** [Your recommendation]
- Insert a horizontal line (---) followed by an empty line break, before each major section header

---

### OUTPUT FORMAT
Here's the required structure of your output. Follow this EXACT format with line breaks:

Here's a priority-based summary of the medical check-up results for **[Patient Name]** :

## 🔴 High Priority (Needs Immediate Follow-Up)
**1. [Medical Finding]** *(simple term)* 
[Key test values] 👉 [One concise sentence explaining what this means and why it's urgent]
✅ **Action:** [Brief, specific next steps]
(Repeat as needed)

—-

## 🟠 Moderate Priority (Monitor & Treat)
**1. [Medical Finding]** *(simple term)* 
[Key test values] 👉 [One sentence explaining what this means for your health]
✅ **Action:** [Brief, practical guidance]
(Repeat as needed)

—-

## 🟡 Low Priority (Routine Monitoring)
**1. [Medical Finding]** *(simple term)* 
[Key test values] 👉 [Brief, reassuring explanation]
✅ **Action:** [Simple monitoring advice]
(Repeat as needed)

—-

## ✅ Normal Findings (No Action Needed)
**[Category of Normal Tests/Results]**
👉 [Brief, reassuring explanation of what these normal results mean for your health and why they're good news]
(Group related findings and repeat as needed)

—-

## 📝 Doctor's Summary
**Conclusion:** "[Translate and summarize the doctor's final conclusion]"
**Recommendations:** [Any referrals or follow-ups listed]
    `,
  },
];

const MAX_CONVERSATION_STARTERS = 3;

export const ChatWithEluveGpt = ({
  tenantId,
  baseUrl,
}: {
  tenantId: string;
  baseUrl: string;
}) => {
  const userId = useUserIdFromSession();
  const { data } = useSuspenseQuery(getChatConversationStartersQuery, {
    variables: { tenantId, userId },
  });

  const customConversationStarters =
    data.tenantUserChatSettingsByPk?.conversationStarters ?? [];

  const chatAgentPromptResponse = useFeatureFlagPayload('CHAT_AGENT_PROMPTS');
  const flagStartingMessages =
    chatAgentPromptResponse?.insightChatStartingMessages ??
    ([] as StartingChatMessage[]);

  // Custom starters - > Starters Flag -> Default starters
  const startingMessages =
    customConversationStarters?.length > 0
      ? customConversationStarters
      : flagStartingMessages?.length > 0
        ? flagStartingMessages
        : DEFAULT_STARTING_MESSAGES;

  return (
    <ChatComponent
      tenantId={tenantId}
      baseUrl={baseUrl}
      config={{
        startingMessages: startingMessages.slice(0, MAX_CONVERSATION_STARTERS),
      }}
    />
  );
};
